import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Rocket, Target, Zap, Shield, ChevronDown, Search, Brain } from 'lucide-react';
import VideoBg from '../components/layout/VideoBg';

const HomePage = () => {
    const navigate = useNavigate();
    const [offsetY, setOffsetY] = useState(0);

    const handleScroll = () => {
        setOffsetY(window.pageYOffset);
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <div style={{ backgroundColor: '#05070A', color: 'white', overflowX: 'hidden', position: 'relative' }}>
            {/* Background Parallax Video/Asset */}
            <VideoBg 
                src="/mars_scan.webp" 
                poster="/opportunityscan.webp" 
                parallaxOffset={offsetY} 
            />

            {/* 1. Hero Section */}
            <section style={{
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                position: 'relative',
                overflow: 'hidden',
                zIndex: 1
            }}>
                {/* Hero Overlay Gradient */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'radial-gradient(circle, rgba(5, 7, 10, 0) 0%, rgba(5, 7, 10, 0.8) 100%)',
                    zIndex: 1
                }} />

                <div style={{ textAlign: 'center', zIndex: 2 }}>
                    <h1 style={{
                        fontSize: 'max(5vw, 40px)',
                        fontWeight: '900',
                        letterSpacing: '8px',
                        margin: '0',
                        fontFamily: "'Orbitron', sans-serif",
                        textShadow: '0 0 20px rgba(11, 61, 145, 0.8)'
                    }}>
                        OPPORTUNITY
                    </h1>
                    <p style={{
                        fontSize: '1.2rem',
                        color: '#E27B58',
                        letterSpacing: '3px',
                        textTransform: 'uppercase',
                        marginTop: '10px'
                    }}>
                        Geo-Intelligent Site Selection System
                    </p>
                    <div style={{ marginTop: '50px', display: 'flex', gap: '20px', justifyContent: 'center' }}>
                        <button
                            onClick={() => navigate('/login')}
                            className="nasa-button"
                            style={{ fontSize: '1rem', padding: '15px 40px' }}
                        >
                            LAUNCH MISSION
                        </button>
                        <button 
                            onClick={() => window.location.href = '/info'}
                            className="nasa-button-docs" 
                            style={{ fontSize: '1rem', padding: '15px 40px' }}
                        >
                            DOCUMENTATION
                        </button>
                    </div>
                </div>

                <div style={{ position: 'absolute', bottom: '30px', animation: 'bounce 2s infinite' }}>
                    <ChevronDown size={32} color="#0B3D91" />
                </div>
            </section>

            {/* 2. Mission Story Section */}
            <section style={{ padding: '100px 20px', maxWidth: '1000px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
                <div style={{
                    display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '60px', alignItems: 'center',
                    padding: '10px 20px 60px 20px',
                    backgroundColor: 'rgba(5, 7, 10, 0.85)',
                    position: 'relative',
                    zIndex: 1,
                    backdropFilter: 'blur(10px)',
                    borderTop: '1px solid #1A1A1A',
                    borderBottom: '1px solid #1A1A1A'
                }}>
                    <div>
                        <h2 style={{ color: '#0B3D91', fontSize: '2rem', marginBottom: '25px', letterSpacing: '2px' }}>
                            Localizando Oportunidades para situar una delegación de tu marca
                        </h2>
                        <p style={{ color: '#AAA', lineHeight: '1.8', fontSize: '1.1rem' }}>
                            Hace 60 años que la Mariner IV sobrevolo Marte, y posteriormente en 1971, Mars 2 y Mars 3 aterrizaron sobre un lugar adecuado en la superficie marciana.
                        </p>
                        <p style={{ color: '#AAA', lineHeight: '1.8', fontSize: '1.1rem' }}>
                            Por lo que seguro que es más facil elegir el lugar adecuado para tu marca de como lo haces hasta ahora.
                        </p>
                        <p style={{ color: '#AAA', lineHeight: '1.8', fontSize: '1.1rem', marginTop: '20px' }}>
                            Hemos combinado la metodología PASTA V, el modelo HUFF, Twin Areas y puntuación con CapEx con modelos avanzados de GeoAI para asegurar que tu inversión aterrice donde el éxito es matemáticamente predecible.
                        </p>
                    </div>
                    <div style={{
                        border: '1px solid #0B3D91',
                        padding: '10px',
                        background: 'rgba(11, 61, 145, 0.05)',
                        position: 'relative'
                    }}>
                        <img
                            src="/mapa.jpeg"
                            alt="Mars Terrain"
                            style={{ width: '100%', opacity: 0.8 }}
                        />
                        <div style={{
                            position: 'absolute',
                            top: '20px',
                            right: '20px',
                            border: '1px solid #E27B58',
                            color: '#E27B58',
                            fontSize: '0.6rem',
                            padding: '2px 5px',
                            textTransform: 'uppercase'
                        }}>
                            DATA SCAN ACTIVE
                        </div>
                    </div>
                </div>
            </section>

            {/* 3. Core Systems (Cards) */}
            <section style={{ padding: '100px 20px', backgroundColor: 'rgba(10, 12, 15, 0.7)', position: 'relative', zIndex: 1, backdropFilter: 'blur(5px)' }}>
                <div style={{ textAlign: 'center', marginBottom: '60px' }}>
                    <h2 style={{ letterSpacing: '5px', textTransform: 'uppercase' }}>Core Systems</h2>
                </div>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    gap: '30px',
                    maxWidth: '1200px',
                    margin: '0 auto'
                }}>
                    {[
                        { icon: Search, title: 'Scouting', desc: 'Deja que los sistemas de cribado de datos busquen todas las mejores opciones para ubicar tu negocio.' },
                        { icon: Target, title: 'PASTA V', desc: 'Puntuación de ubicaciones con Parking, Access, Signing, Traffic, Activity, and Visibility.' },
                        { icon: Brain, title: 'Huff Model', desc: 'Predice la posibilidad de capturar visitantes con modelos calibrados de fricción.' },
                        { icon: Zap, title: 'Turbo Import', desc: 'Ingesta instantánea de datos de marca multi-país (España, Marruecos, UE).' }
                    ].map((feature, i) => (
                        <div key={i} style={{
                            padding: '40px',
                            border: '1px solid #1A1A1A',
                            background: 'rgba(5, 7, 10, 0.8)',
                            transition: 'transform 0.3s',
                            cursor: 'default'
                        }} className="feature-card">
                            <feature.icon size={32} color="#0B3D91" style={{ marginBottom: '20px' }} />
                            <h3 style={{ color: '#E27B58', fontSize: '1rem', marginBottom: '15px' }}>{feature.title}</h3>
                            <p style={{ color: '#888', fontSize: '0.9rem', lineHeight: '1.6' }}>{feature.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* 4. Footer */}
            <footer style={{
                padding: '50px 20px',
                borderTop: '1px solid #1A1A1A',
                textAlign: 'center',
                color: '#444',
                fontSize: '0.8rem',
                position: 'relative',
                zIndex: 1,
                backgroundColor: 'rgba(5, 7, 10, 0.9)'
            }}>
                <div style={{ marginBottom: '20px' }}>
                    <Rocket size={24} color="#0B3D91" style={{ margin: '0 auto' }} />
                </div>
                SYSTEM VERSION 1.0.0 | MISSION CONTROL: SPAIN<br />
                NO DATA FROM MARS WAS HARMED DURING DEVELOPMENT
            </footer>

            <style>{`
        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% {transform: translateY(0);}
          40% {transform: translateY(-10px);}
          60% {transform: translateY(-5px);}
        }
        .feature-card:hover {
          transform: translateY(-10px);
          border-color: #0B3D91 !important;
        }
      `}</style>
        </div>
    );
};

export default HomePage;
